import { GetServerSideProps } from 'next';
import { z } from 'zod';
import cms from '../lib/contentful';
import { NewsItem } from '../lib/types';
import NewsPage, { humanFriendlyTypes, NewsPageData, NewsPageProps } from '../src/components/NewsPage';
import { NEWS_POSTS_PAGINATION_PAGE_SIZE } from '../src/Config';

// Type for all keys in NewsPageData that are NewsItems
export type NewsItemsKeys = keyof Pick<NewsPageData, {
  [K in keyof NewsPageData]: NewsPageData[K] extends NewsItem ? K : never
}[keyof NewsPageData]>;

export const newsSchema = z.object({
  type: z.string().default('').refine((str) => ['', 'news', 'press-release', 'event'].includes(str)),
  page: z.coerce.number().default(1),
});

export const getServerSideProps: GetServerSideProps<
NewsPageProps> = async (ctx) => {
  try {
    const { page, type } = await newsSchema.parseAsync(ctx.query);
    const resourceCount = await cms.getTotalNumberOfNews(type || undefined);
    const featureParts: {[k: string]: NewsItem[]} = {};

    if (!type) {
      // eslint-disable-next-line no-restricted-syntax
      for (const [key] of Object.entries(humanFriendlyTypes)) {
        // eslint-disable-next-line no-await-in-loop
        const feature = await cms.getNewsPosts(key, 0, 4);
        featureParts[key] = feature;
      }
    } else {
      featureParts[type] = await cms.getNewsPosts(
        type,
        page - 1,
      );
    }

    const newsPageProps = await cms.getNewsPage();

    const featuredForTypes: {[k: string]: NewsItemsKeys | undefined} = {
      news: 'featuredNews',
      'press-release': 'featuredPressRelease',
      event: 'featuredEvent',
      '': 'featuredAll',
    };

    const featuredPost = newsPageProps[featuredForTypes[type] || 'featuredAll'] || newsPageProps.featuredAll;

    const pageCount = Math.ceil(resourceCount / NEWS_POSTS_PAGINATION_PAGE_SIZE);
    const navbarData = await cms.getNavbar();

    return {
      props: {
        ...newsPageProps,
        navbarData: navbarData.menu,
        featuredPost,
        newsData: {
          pageCount,
          currentPage: page,
          currentNews: featureParts,
          type,
          query: ctx.query,
        },
      },
    };
  } catch (e) {
    if (e instanceof z.ZodError) {
      return { notFound: true };
    }
    throw e;
  }
};

export default NewsPage;
